import api from 'api';
import { useState, useEffect } from 'react';
import { Outlet, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'redux/store';
import { Client } from 'api/models/response/ClientList';
import { loadApplications } from 'redux/applications/applications-middleware';
import { loadApp } from 'redux/application/application.middleware';
import { useWindowSize } from 'hooks/useWindowSize';
import { HeaderMainSection } from '../../Toggle.styled';
import SelectClientSearchBar from './components/ClientProfile/components/SelectClientSearchBar';
import RelatedProfileSwitcher from './components/ClientProfile/components/RelatedProfileSwitcher';
import { useSpouseProfile } from 'hooks/useSpouseProfile';
import { useSpouseUserId } from 'hooks/useSpouseUserId';
import {
    RELATED_FROM_QUERY_KEY,
    formatRelatedPersonName,
    getRelatedSwitcherName,
} from './components/ClientProfile/components/relatedProfile';
import * as S from '../../CLIENTFLOW/agreement/Agreement.styled';
import { useTranslation } from 'react-i18next';
import AssessmentDownloadModal from '../shared/AssessmentDownloadModal/AssessmentDownloadModal';
// import SelectVisaProgramDropdown from '../../select-retainer-type-dropdown/SelectSOPTypeDropdown';
import { ReactComponent as AIIcon } from '../../../../../assets/icons/AI.svg';
import MultiStepModal from './components/SOPWriter/SOPWriter';
import { ClientViewShell, DocumentActionButton, DocumentHeaderActions } from './ClientView.styled';
import PERMISSION from '../../../../../assets/permissions.json';
import { Permission } from 'api/models/response/permissions';
import OnboardingAgreementWrapper from '../MYCLIENTS/components/dashboard/salesOnboarding/views/AgreementModal/OnboardingAgreementWrapper';
import { addPageNumbersToPdf } from '../../../../../utils/pdfHelpers';
import MuiButton from '@mui/material/Button';

const PlaceholderClient = {
    firstname: '',
    id: 0,
    lastname: '',
    profile_picture_id: 0,
};

const ClientView = () => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const params = useParams();
    const { id: candidateID, appId: appID } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const { applications: clientApps } = useSelector((state: RootState) => state.applications);
    const [renderCount, setRenderCount] = useState(0);
    const [currentClient, setCurrentClient] = useState<Client>(PlaceholderClient);
    const [originatingClient, setOriginatingClient] = useState<Client | null>(null);
    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);
    const [showClientView, setShowClientView] = useState(false);
    // const [showSOPPage, setShowSOPPage] = useState(false);
    const [open, setShowSOPPage] = useState(false); // ✅ Ensure open is a boolean
    const [showCreateNewAgreementPage, setShowCreateNewAgreementPage] = useState(false);
    const isDocumentsPage = params['*']?.endsWith('/documents');
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const isResultsPage = location.pathname.includes('/results');
    const isAgreementListPage = location.pathname.includes('/agreement');
    const isClientProfilePage = location.pathname.includes('/client-profile');
    const isProfileIndex = /\/client-profile\/?$/.test(location.pathname);
    const relatedFromParam = searchParams.get(RELATED_FROM_QUERY_KEY);
    const relatedFromId = relatedFromParam ? Number(relatedFromParam) : NaN;
    const isRelatedProfileActive = Number.isFinite(relatedFromId) && relatedFromId > 0;
    const profileCandidateId = Number(candidateID);
    const validProfileCandidateId =
        Number.isFinite(profileCandidateId) && profileCandidateId > 0
            ? profileCandidateId
            : undefined;
    const spouseProfile = useSpouseProfile(validProfileCandidateId, {
        enabled:
            isClientProfilePage && validProfileCandidateId !== undefined && !isRelatedProfileActive,
    });
    const { spouseUserId: additionalDetailsSpouseId } = useSpouseUserId(
        isClientProfilePage && !isRelatedProfileActive ? validProfileCandidateId : undefined
    );
    const spouseUserId = additionalDetailsSpouseId ?? spouseProfile.spouse?.user_id ?? null;
    const [paymentStatus, setPaymentStatus] = useState<string>('');
    const [, setToggleChecked] = useState<boolean>(false);
    const [, setDisableToggle] = useState<boolean>(true);

    // when user lands here from the url provided in the mail (notification)
    useEffect(() => {
        if (appID) {
            dispatch(loadApp(appID));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appID]);

    // ***allow application to persist on page refresh
    useEffect(() => {
        const storedActiveApp = sessionStorage.getItem('activeApp');
        if (storedActiveApp) {
            dispatch(loadApp(storedActiveApp));
        }
    }, [dispatch]);

    useEffect(() => {
        if (activeApp) {
            sessionStorage.setItem('activeApp', activeApp.id.toString());
        }
    }, [activeApp]);

    useEffect(() => {
        //this automatically sets the dropdown menu to the fist case when a new client is selected from the dropdown.
        //only after render 2 so that it does not override the application selected from clients table on the main dashboard
        if (renderCount > 2 && clientApps.length > 0) {
            dispatch(loadApp(clientApps[0].id.toString()));
        }
        //eslint-disable-next-line
    }, [clientApps, renderCount]);

    useEffect(() => {
        //this is needed if the user has clicked back to dashboard and that clients applications are already loaded, it prevents the render count from increased and selecting the first app
        if (clientApps.length && clientApps[0].candidate_id.toString() === candidateID) {
            console.log('nothing is needed');
        } else {
            if (candidateID) {
                dispatch(loadApplications(candidateID));
            }
        }
        getAssessmentStatus(candidateID);
        //eslint-disable-next-line
    }, [candidateID]);

    // Keep renderCount in sync with clientApps so the effect that auto-selects
    // the first case (above) still runs only after the first two renders. The
    // dropdown that previously consumed `clientApps` now lives in QuickActions
    // and reads directly from redux's `applications` slice, so there is no
    // longer any derived state to maintain here.
    useEffect(() => {
        if (clientApps.length && clientApps[0].candidate_id.toString() === candidateID) {
            setRenderCount((prevCount) => prevCount + 1);
        }
        //eslint-disable-next-line
    }, [clientApps]);

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    useEffect(() => {
        if (width >= 1066) {
            setShowClientView(true);
        }
        if (width < 1066) {
            setShowClientView(false);
        }
    }, [width, height]);

    useEffect(() => {
        if (candidateID) {
            if (currentClient) {
                if (currentClient?.id.toString() !== candidateID) {
                    getClient(Number(candidateID));
                }
            }
            getClient(Number(candidateID));
        }
        //eslint-disable-next-line
    }, [candidateID]);

    useEffect(() => {
        if (!isRelatedProfileActive) {
            setOriginatingClient(null);
            return;
        }

        let cancelled = false;

        const loadOriginatingClient = async () => {
            try {
                const resp = await api.clientList.getClientById(relatedFromId);
                if (!cancelled && (resp.status === 200 || resp.status === 201) && resp.data) {
                    setOriginatingClient(resp.data);
                }
            } catch (err) {
                console.error(err);
            }
        };

        void loadOriginatingClient();

        return () => {
            cancelled = true;
        };
    }, [isRelatedProfileActive, relatedFromId]);

    const getClient = async (id: number) => {
        try {
            const resp = await api.clientList.getClientById(id);
            if (resp.status === 200 || resp.status === 201) {
                setCurrentClient(resp.data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (candidateID) {
            if (paymentStatus === 'SUCCESSFUL') {
                setToggleChecked(true);
            } else {
                setToggleChecked(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentStatus]);

    const getAssessmentStatus = async (candidate?: string) => {
        if (candidate) {
            try {
                const resp = await api.assessment_payment.getAssessmentPayment(candidate);
                if (resp.status === 200 || resp.status === 201) {
                    if (Object.keys(resp?.data).length === 0) {
                        setDisableToggle(true);
                        setPaymentStatus('');
                    } else {
                        setDisableToggle(false);
                        if (resp?.data.status) {
                            setPaymentStatus(resp.data.status);
                        }
                    }
                }
            } catch (err) {
                console.error(err);
            }
        }
    };
    const [pdfDoc, setPdfDoc] = useState<string | null>(null);

    const downloadAssessment = async (id: string, country: string, lang: string) => {
        const resp = await api.assessment.getNewAssessment(Number(id), country, lang);
        const numbered = await addPageNumbersToPdf(resp.data);
        prepareBlobFile(numbered);
    };

    const prepareBlobFile = async (file: Blob) => {
        const pdfBlob = new Blob([file], { type: 'application/pdf' });

        const pdfBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(pdfBlob);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });

        if (typeof pdfBase64 === 'string') {
            setPdfDoc(pdfBase64);
        }
    };

    useEffect(() => {
        if (pdfDoc) {
            const link = document.createElement('a');
            link.href = pdfDoc;
            link.download = 'assessment_report.pdf';
            link.click();
        }
        setPdfDoc(null);
    }, [pdfDoc]);

    const [, setSelfAssessment] = useState<boolean>(false);

    useEffect(() => {
        if (candidateID) {
            getPermissions(Number(candidateID));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPermissions = async (candidateId: number) => {
        try {
            const resp = await api.permissions.getPermissions(candidateId);
            if (resp?.data.some((permission: Permission) => permission.name === PERMISSION.EDIT)) {
                setSelfAssessment(true);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const [openCaseProperties, setOpenCaseProperties] = useState<boolean>(false);
    const openCloseCaseProperties = async () => {
        setOpenCaseProperties((prevState) => !prevState);
    };

    const getOpenCaseTemplate = () => {
        return (
            <AssessmentDownloadModal
                open={openCaseProperties}
                closeModal={openCloseCaseProperties}
                handleDownloadReport={downloadAssessment}
            />
        );
    };

    const handleRelatedProfileToggle = () => {
        if (!validProfileCandidateId) {
            return;
        }

        // Return to the originating client (same as re-selecting them in the table).
        if (isRelatedProfileActive) {
            navigate(`/dashboard/${relatedFromId}/client-profile`);
            return;
        }

        // Open the spouse as the active client — their user_id becomes the URL id.
        if (!spouseUserId) {
            return;
        }

        if (currentClient.id === validProfileCandidateId) {
            setOriginatingClient(currentClient);
        }

        navigate(
            `/dashboard/${spouseUserId}/client-profile?${RELATED_FROM_QUERY_KEY}=${validProfileCandidateId}`
        );
    };

    const spouseName = formatRelatedPersonName(
        spouseProfile.spouse?.firstname,
        spouseProfile.spouse?.lastname
    );
    const originatingClientName = formatRelatedPersonName(
        originatingClient?.firstname,
        originatingClient?.lastname
    );
    // Switcher always labels the other person (spouse of whoever is in the Client search field).
    const relatedSwitcherName = getRelatedSwitcherName({
        isRelatedProfileActive,
        originatingClientName,
        spouseName,
        fallback: t('spouse/CommonLawPartner', { lng: language }),
    });
    const showRelatedProfileSwitcher =
        isClientProfilePage &&
        (isRelatedProfileActive || (!!spouseUserId && spouseUserId !== validProfileCandidateId));

    return (
        <>
            {candidateID && (
                <ClientViewShell $fillProfile={isProfileIndex && !mobile}>
                    <HeaderMainSection style={{ alignItems: 'center', width: '100%' }}>
                        <S.Title>
                            {showClientView ? `${t('client', { lng: language })}:` : ''}
                            {currentClient && (
                                <SelectClientSearchBar
                                    currentClient={currentClient}
                                    mobile={mobile}
                                    matchingWidth={mobile ? '145px' : '220px'}
                                ></SelectClientSearchBar>
                            )}
                            {showRelatedProfileSwitcher && (
                                <RelatedProfileSwitcher
                                    name={relatedSwitcherName}
                                    relationship={t('spouse/CommonLawPartner', {
                                        lng: language,
                                        defaultValue: 'Spouse / common-law',
                                    })}
                                    active={isRelatedProfileActive}
                                    activeLabel={t('activeProfile', {
                                        lng: language,
                                        defaultValue: 'Active profile',
                                    })}
                                    actionLabel={
                                        isRelatedProfileActive
                                            ? t('returnToCurrentClient', {
                                                  lng: language,
                                                  defaultValue: 'Return to current client',
                                              })
                                            : t('switchToRelatedProfile', {
                                                  lng: language,
                                                  defaultValue: 'Switch to related profile',
                                              })
                                    }
                                    onToggle={handleRelatedProfileToggle}
                                />
                            )}
                        </S.Title>

                        <span id="this is needed" style={{ height: '36px' }}></span>
                        {isAgreementListPage && (
                            <DocumentHeaderActions>
                                <DocumentActionButton
                                    type="button"
                                    onClick={() => {
                                        navigate('/templates');
                                    }}
                                >
                                    {t('manageTemplates', { lng: language })}
                                </DocumentActionButton>
                            </DocumentHeaderActions>
                        )}
                        {activeApp && isDocumentsPage && (
                            <DocumentHeaderActions
                                style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                            >
                                <MuiButton
                                    variant="outlined"
                                    type="button"
                                    onClick={() => {
                                        setShowSOPPage(true);
                                    }}
                                    startIcon={
                                        language === 'EN' && (
                                            <AIIcon
                                                style={{ color: '#8353e2', marginRight: 2 }}
                                                width={18}
                                                height={18}
                                            />
                                        )
                                    }
                                >
                                    {t('aiWriter', { lng: language })}
                                </MuiButton>
                            </DocumentHeaderActions>
                        )}
                        {isResultsPage && (
                            <>
                                <DocumentHeaderActions>
                                    <DocumentActionButton
                                        type="button"
                                        onClick={openCloseCaseProperties}
                                    >
                                        {t('downloadReport', { lng: language })}
                                    </DocumentActionButton>
                                </DocumentHeaderActions>
                                {getOpenCaseTemplate()}
                            </>
                        )}
                    </HeaderMainSection>
                    <Outlet />

                    <OnboardingAgreementWrapper
                        open={showCreateNewAgreementPage}
                        onClose={() => setShowCreateNewAgreementPage(false)}
                    />
                </ClientViewShell>
            )}

            <MultiStepModal open={open} onClose={() => setShowSOPPage(false)} />
        </>
    );
};

export default ClientView;
